/* REACT */
import { useState } from 'react';

/* ROUTER */
import { Link } from 'react-router-dom';

/* PLUGINS */
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';

/* HOOKS */
import { useLogin } from '../hooks';

/* SCHEMA */
import { loginSchema, type LoginFormData } from '../schemas';

/* COMPONENTS */
import { AuthLayout } from './AuthLayout';

const AUTH_CARD_CLASS = [
    'w-full max-w-[480px] relative rounded-lg border border-[#e5e5e5] bg-white',
    'flex flex-col items-center px-8 py-8 md:px-10 gap-8 text-center',
].join(' ');
const INPUT_CLASS = [
    'flex-1 text-sm leading-5 tracking-[0.25px] border-none outline-none',
    'bg-transparent text-[#281e1b] placeholder:text-[rgba(46,27,20,0.4)]',
    'disabled:cursor-not-allowed',
].join(' ');
const LOGIN_BUTTON_CLASS = [
    'self-stretch rounded-lg bg-brand flex items-center justify-center px-4 py-3',
    'cursor-pointer text-white border-none text-sm leading-5 tracking-[0.25px]',
    'font-medium transition-colors duration-200 hover:bg-brand-hover',
    'disabled:bg-[#e5e5e5] disabled:text-[rgba(46,27,20,0.4)]',
    'disabled:cursor-not-allowed',
].join(' ');
const LABEL_CLASS = [
    'self-stretch text-sm leading-5 tracking-[0.25px]',
    'font-medium text-[#281e1b]',
].join(' ');
const FIELD_ERROR_CLASS = 'text-xs leading-4 tracking-[0.25px] text-[#dc2626] mt-1';
const ICON_BUTTON_CLASS = [
    'bg-transparent border-none cursor-pointer p-0 flex items-center',
    'justify-center text-[#363636] hover:opacity-70',
    'transition-opacity disabled:cursor-not-allowed',
].join(' ');
const FORGOT_LINK_CLASS = [
    'self-start text-sm leading-5 tracking-[0.25px] text-brand',
    'no-underline hover:underline cursor-pointer',
].join(' ');
const ERROR_ALERT_CLASS = [
    'self-stretch p-3 bg-[#fee2e2] border border-[#dc2626] rounded-lg',
    'text-sm leading-5 text-[#dc2626]',
].join(' ');

interface LoginApiError {
    response?: {
        data?: {
            error?: {
                message?: string;
            };
            message?: string;
        };
    };
}

/**
 * DOCU: Renders the PO-style login form while preserving the current login mutation. <br>
 * Triggered: When user navigates to /login or /signin. <br>
 * Last Updated Date: May 19, 2026 <br>
 * @returns {JSX.Element} Login screen.
 * @author Sam
 */
export const LoginForm = () => {
    const { mutate: login, isPending, error, reset } = useLogin();
    const [show_password, setShowPassword] = useState(false);
    const {
        formState: { errors },
        handleSubmit,
        register,
    } = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema),
    });

    /**
     * DOCU: Submit login credentials to the existing auth mutation. <br>
     * Triggered: Login form submit. <br>
     * Last Updated Date: May 19, 2026 <br>
     * @param {LoginFormData} data - Login credentials.
     * @returns {void}
     * @author Sam
     */
    const onSubmit = (data: LoginFormData): void => {
        login(data);
    };

    const login_error = error as LoginApiError | null;
    const error_message = login_error?.response?.data?.error?.message
        || login_error?.response?.data?.message
        || 'Invalid Sign In Credentials.';
    const email_field_class = [
        'self-stretch rounded-lg border bg-white flex items-center px-3 py-2',
        'transition-all duration-200 focus-within:border-brand focus-within:border-[1.5px]',
        errors.email_address ? 'border-[#dc2626] border-[1.5px]' : 'border-[#e5e5e5]',
    ].join(' ');
    const password_field_class = [
        'self-stretch rounded-lg border bg-white flex items-center px-3 py-2',
        'transition-all duration-200 focus-within:border-brand focus-within:border-[1.5px]',
        errors.password ? 'border-[#dc2626] border-[1.5px]' : 'border-[#e5e5e5]',
    ].join(' ');

    return (
        <AuthLayout title="">
            <div className={AUTH_CARD_CLASS}>
                <div className="self-stretch flex flex-col items-center gap-2">
                    <h1 className="self-stretch text-[28px] leading-9 font-medium text-[#363636]">
                        Sign In
                    </h1>
                    <p className="self-stretch text-base leading-6 tracking-[0.5px] text-[#281e1b]">
                        Enter your credentials to access your account.
                    </p>
                </div>

                <form
                    className={[
                        'self-stretch flex flex-col items-start gap-4 text-left',
                        'text-sm text-[#281e1b]',
                    ].join(' ')}
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <div className="self-stretch flex flex-col items-start gap-2">
                        <label
                            htmlFor="email_address"
                            className={LABEL_CLASS}
                        >
                            Email Address
                        </label>
                        <div className={email_field_class}>
                            <input
                                id="email_address"
                                type="email"
                                placeholder="your.email@company.com"
                                disabled={isPending}
                                className={INPUT_CLASS}
                                {...register('email_address', {
                                    onChange: () => {
                                        if (error) reset();
                                    },
                                })}
                            />
                        </div>
                        {errors.email_address && (
                            <div className={FIELD_ERROR_CLASS}>
                                {errors.email_address.message}
                            </div>
                        )}
                    </div>

                    <div className="self-stretch flex flex-col items-start gap-2">
                        <label
                            htmlFor="password"
                            className={LABEL_CLASS}
                        >
                            Password
                        </label>
                        <div className={password_field_class}>
                            <input
                                id="password"
                                type={show_password ? 'text' : 'password'}
                                placeholder="Enter your password"
                                disabled={isPending}
                                className={INPUT_CLASS}
                                {...register('password', {
                                    onChange: () => {
                                        if (error) reset();
                                    },
                                })}
                            />
                            <button
                                type="button"
                                className={ICON_BUTTON_CLASS}
                                onClick={() => setShowPassword(!show_password)}
                                disabled={isPending}
                                aria-label={show_password ? 'Hide password' : 'Show password'}
                            >
                                {show_password ? (
                                    <EyeOff className="size-5" aria-hidden="true" />
                                ) : (
                                    <Eye className="size-5" aria-hidden="true" />
                                )}
                            </button>
                        </div>
                        {errors.password && (
                            <div className={FIELD_ERROR_CLASS}>
                                {errors.password.message}
                            </div>
                        )}
                    </div>

                    <Link
                        to="/forgot-password"
                        className={FORGOT_LINK_CLASS}
                    >
                        Forgot Password?
                    </Link>

                    {error && (
                        <div className={ERROR_ALERT_CLASS}>
                            {error_message}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={isPending}
                        className={LOGIN_BUTTON_CLASS}
                    >
                        {isPending ? (
                            <span className="flex items-center gap-2">
                                <Loader2 className="size-5 animate-spin" aria-hidden="true" />
                                Signing In...
                            </span>
                        ) : (
                            'Sign In'
                        )}
                    </button>
                </form>

                <div className="self-stretch text-center text-sm leading-5 text-[#737373]">
                    Don&apos;t have an account?{' '}
                    <Link
                        to="/register"
                        className="font-medium text-brand hover:underline"
                    >
                        Create Account
                    </Link>
                </div>
            </div>
        </AuthLayout>
    );
};
